import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { PencilSimple, Trash, ArrowDownLeft, ArrowUpRight, Receipt, Printer, DownloadSimple, ArrowLeft, Handshake, Sparkle } from "@phosphor-icons/react";
import ReconciliationDialog from "@/components/ReconciliationDialog";
import CollectionMessageDialog from "@/components/CollectionMessageDialog";
import { Button } from "@/components/ui/button";
import { PageHeader, Kpi, Th, Td, Empty, Tag, Balance, Confirm, DateRange } from "@/components/common";
import AccountDialog from "@/components/AccountDialog";
import PaymentDialog from "@/components/PaymentDialog";
import Attachments from "@/components/Attachments";
import { api, money, fmtDate, L, errMsg, downloadCSV, SYM, num } from "@/lib/api";
import { useLoad } from "@/lib/useLoad";

const refLink = (m) => (m.type.endsWith("invoice") ? `/faturalar/${m.ref_id}` : null);

export default function AccountDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [edit, setEdit] = useState(false);
  const [pay, setPay] = useState(null);
  const [recon, setRecon] = useState(false);
  const [collect, setCollect] = useState(false);
  const [acc, reloadAcc] = useLoad(() => api.get(`/accounts/${id}`).then((r) => r.data), [id]);
  const [st, reloadSt] = useLoad(() => api.get(`/accounts/${id}/statement`, { params: { start: start || undefined, end: end || undefined } }).then((r) => r.data), [id, start, end]);
  const reload = () => { reloadAcc(); reloadSt(); };

  const del = async () => {
    try {
      await api.delete(`/accounts/${id}`);
      toast.success("Cari silindi");
      nav("/cariler");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const csv = () => downloadCSV(`ekstre-${acc.code}.csv`,
    [{ key: "date", label: "Tarih", type: "date" }, { key: "ref_no", label: "Belge" }, { key: "description", label: "Açıklama" },
      { key: "debit", label: "Borç", type: "money" }, { key: "credit", label: "Alacak", type: "money" }, { key: "balance", label: "Bakiye", type: "money" }], st.rows);

  if (!acc) return <div className="label-eyebrow">Yükleniyor…</div>;
  const isSup = acc.type === "supplier";
  return (
    <div>
      <Link to="/cariler" className="no-print mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-accounts"><ArrowLeft size={14} /> Cari Hesaplar</Link>
      <PageHeader eyebrow={`${acc.code} · ${L.accType[acc.type]}`} title={acc.name} testid="account-detail-name">
        <Button variant="outline" className="rounded-[4px]" onClick={() => setPay("collection")} data-testid="account-collect-button"><ArrowDownLeft /> Tahsilat Al</Button>
        <Button variant="outline" className="rounded-[4px]" onClick={() => setPay("payment")} data-testid="account-pay-button"><ArrowUpRight /> Ödeme Yap</Button>
        <Button asChild className="rounded-[4px]" data-testid="account-new-invoice-button">
          <Link to={`/faturalar/yeni?tur=${isSup ? "purchase" : "sale"}&cari=${id}`}><Receipt /> {isSup ? "Alış Faturası" : "Satış Faturası"}</Link>
        </Button>
        <Button variant="outline" className="rounded-[4px] border-[#fdba74] text-orange-800" onClick={() => setCollect(true)} data-testid="account-collection-message-button"><Sparkle weight="fill" /> Tahsilat Mesajı</Button>
        <Button variant="outline" className="rounded-[4px] border-[#2dd4bf] text-teal-800" onClick={() => setRecon(true)} data-testid="account-reconciliation-button"><Handshake weight="fill" /> Mutabakat</Button>
        <Button variant="ghost" size="icon" onClick={() => setEdit(true)} data-testid="account-edit-button"><PencilSimple /></Button>
        <Confirm title="Cari silinsin mi?" description="Bu işlem geri alınamaz. Faturası olan cariler silinemez." onConfirm={del} testid="account-delete-confirm"
          trigger={<Button variant="ghost" size="icon" className="text-red-600" data-testid="account-delete-button"><Trash /></Button>} />
      </PageHeader>

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Kpi label="Toplam Borç" value={money(acc.debit)} testid="account-total-debit" />
        <Kpi label="Toplam Alacak" value={money(acc.credit)} testid="account-total-credit" delay={40} />
        <div className="panel lift fade-up p-5" data-testid="account-balance">
          <span className="label-eyebrow">Bakiye</span>
          <div className="mt-3 text-2xl font-semibold"><Balance v={acc.balance} /></div>
          <div className="mt-1 text-xs text-muted-foreground">{acc.balance > 0 ? "Cari bize borçlu" : acc.balance < 0 ? "Cariye borçluyuz" : "Bakiye kapalı"}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
        <div className="panel p-5 text-sm lg:col-span-1" data-testid="account-info">
          <h3 className="mb-4 font-display text-lg font-bold">Bilgiler</h3>
          {[["Vergi Dairesi", acc.tax_office], ["Vergi / TC No", acc.tax_no], ["Telefon", acc.phone], ["E-posta", acc.email], ["Adres", acc.address], ["Not", acc.notes]].map(([l, v]) => (
            <div key={l} className="mb-3"><div className="label-eyebrow">{l}</div><div className="mt-0.5 break-words">{v || "—"}</div></div>
          ))}
        </div>
        <div className="panel lg:col-span-3">
          <div className="flex flex-col gap-3 border-b border-border p-4 md:flex-row md:items-center md:justify-between">
            <h3 className="font-display text-lg font-bold">Hesap Ekstresi</h3>
            <div className="no-print flex flex-wrap items-center gap-2">
              <DateRange start={start} end={end} onStart={setStart} onEnd={setEnd} prefix="statement" />
              <Button variant="outline" size="sm" onClick={csv} disabled={!st} data-testid="statement-csv-button"><DownloadSimple /> CSV</Button>
              <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="statement-print-button"><Printer /> Yazdır</Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="statement-table">
              <thead><tr><Th>Tarih</Th><Th>İşlem</Th><Th>Belge</Th><Th>Açıklama</Th><Th right>Borç</Th><Th right>Alacak</Th><Th right>Bakiye</Th></tr></thead>
              <tbody>
                {st && start && <tr className="bg-muted/30"><Td colSpan={6} className="text-xs text-muted-foreground">Devreden bakiye</Td><Td right><Balance v={st.opening} /></Td></tr>}
                {st && st.rows.length === 0 && <Empty cols={7} text="Bu aralıkta hareket yok" />}
                {st?.rows.map((m) => (
                  <tr key={m.id} className="hover:bg-muted/40">
                    <Td className="font-num text-xs">{fmtDate(m.date)}</Td>
                    <Td><Tag tone={m.type === "collection" ? "collection" : m.type === "payment" ? "payment" : m.type === "sale_invoice" ? "sale" : m.type === "purchase_invoice" ? "purchase" : "neutral"}>{L.accMove[m.type]}</Tag></Td>
                    <Td className="font-num text-xs">{refLink(m) ? <Link className="text-primary hover:underline" to={refLink(m)}>{m.ref_no}</Link> : m.ref_no}</Td>
                    <Td className="text-muted-foreground">{m.description}{m.currency !== "TRY" && <span className="ml-1 font-num text-[11px]">({num(m.fx_amount)} {SYM[m.currency]})</span>}</Td>
                    <Td right>{m.debit ? num(m.debit) : ""}</Td>
                    <Td right>{m.credit ? num(m.credit) : ""}</Td>
                    <Td right><Balance v={m.balance} /></Td>
                  </tr>
                ))}
              </tbody>
              {st && st.rows.length > 0 && (
                <tfoot><tr className="bg-muted/60 font-semibold"><Td colSpan={4}>Toplam</Td><Td right>{num(st.debit)}</Td><Td right>{num(st.credit)}</Td><Td right><Balance v={st.balance} /></Td></tr></tfoot>
              )}
            </table>
          </div>
        </div>
      </div>
      <div className="mt-6"><Attachments entityType="account" entityId={id} title="Cari Belgeleri" /></div>
      <AccountDialog open={edit} onOpenChange={setEdit} initial={acc} onSaved={reload} />
      <PaymentDialog open={!!pay} onOpenChange={(o) => !o && setPay(null)} kind={pay || "collection"} accountId={id} onSaved={reload} />
      <ReconciliationDialog open={recon} onOpenChange={setRecon} account={acc} />
      <CollectionMessageDialog open={collect} onOpenChange={setCollect} account={acc} />
    </div>
  );
}
